import { useEffect, useState } from "react";
import { api } from "../api";
import type { Coords, ListMode, VenueListItem } from "../types";
import VenueCard from "../components/VenueCard";

/**
 * 三個入口共用同一個列表：① 附近餐廳（全部菜式，按距離）② 揀完菜式之後
 * ③ 用我嘅到訪記錄篩。
 * 冇結果唔可以靜靜當冇 —— 要講埋最近一間喺邊，因為分母係全 app 唔係你企喺邊。
 */
type ListProps = {
  mode: ListMode;
  coords: Coords;
  cuisineLabel?: string;
  onOpen: (id: number) => void;
  onBack: () => void;
};

/** 後端預設 limit 係 200，但 seed 有 397 間 —— 唔講清楚就會靜靜截斷。 */
const LIST_LIMIT = 1000;

export default function List({ mode, coords, cuisineLabel, onOpen, onBack }: ListProps) {
  const [items, setItems] = useState<VenueListItem[] | null>(null);
  const [all, setAll] = useState<VenueListItem[] | null>(null);
  const [openNow, setOpenNow] = useState(false);
  const [err, setErr] = useState("");

  const cuisineKey = mode.kind === "cuisine" ? mode.key : undefined;
  const districtKey = mode.kind === "cuisine" ? mode.district : undefined;
  const filters = mode.kind === "nearby" ? undefined : mode.filters;
  const filterLabel = mode.kind === "nearby" ? undefined : mode.filterLabel;

  // filters 係物件，直接放入 dep 會每次都新 → 無限重載。要序列化。
  const fKey = JSON.stringify(filters ?? {});

  useEffect(() => {
    let live = true;
    setItems(null);
    setErr("");
    api
      .venues({
        coords, cuisine: cuisineKey, district: districtKey,
        ...(filters ?? {}), openNow, limit: LIST_LIMIT,
      })
      .then((v) => live && setItems(v))
      .catch((e) => live && setErr(e instanceof Error ? e.message : "載入失敗"));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuisineKey, districtKey, fKey, coords?.lat, coords?.lng, openNow]);

  // 冇結果時用嚟報「最近一間喺邊」
  useEffect(() => {
    if (!items || items.length > 0) return;
    api.venues({ coords, limit: LIST_LIMIT }).then(setAll).catch(() => {});
  }, [items, coords?.lat, coords?.lng]);

  const sorted = coords ? "按距離排" : "按露天位最遲收排";
  const title =
    mode.kind === "nearby"
      ? `${coords ? "附近" : "露天位"}${items ? ` ${items.length} 間` : ""}`
      : mode.kind === "visit"
        ? `我記錄過嘅${items ? ` ${items.length} 間` : ""}`
        : `${cuisineLabel ?? cuisineKey}${districtKey ? `　${districtKey}` : ""}` +
          `${items ? `　${items.length} 間` : ""}`;

  return (
    <>
      <div className="sign sign--compact">
        <button className="sign__where" onClick={onBack} style={{ opacity: 0.9 }}>
          ← 返回
        </button>
        <div className="sign__title">{title}</div>
        <div className="sign__sub">
          {mode.kind === "nearby" ? `全部菜式　·　${sorted}` : sorted}
          　·　全部有官方露天位
        </div>
        {filterLabel && <div className="sign__sub">我嘅記錄：{filterLabel}</div>}
      </div>

      <div className="app__body">
        <div className="opts" style={{ margin: "12px 0" }}>
          <button
            className={`opts__item ${openNow ? "opts__item--on" : ""}`}
            onClick={() => setOpenNow((v) => !v)}
            aria-pressed={openNow}
          >
            只顯示開緊嘅
          </button>
        </div>

        {err && <div className="err">{err}</div>}
        {items === null ? (
          <div className="loading">載入緊…</div>
        ) : items.length === 0 ? (
          <div className="empty">
            <b>{mode.kind === "visit" ? "冇一間對得上你嘅記錄。" : "呢一類附近冇。"}</b>
            <br />
            {all && all.length > 0 ? (
              <>
                最近一間要去 {all[0].district ?? all[0].name}
                {all[0].distance_m != null && `（${(all[0].distance_m / 1000).toFixed(1)} 公里）`}
                <br />
                <button className="btn btn--ghost" style={{ marginTop: 14 }}
                  onClick={() => onOpen(all[0].id)}>
                  睇最近嗰間
                </button>
              </>
            ) : (
              "試下唔好開「只顯示開緊嘅」。"
            )}
            {mode.kind === "visit" && (
              <div style={{ marginTop: 12, fontSize: 12, color: "var(--ink2)" }}>
                放寬一個條件通常就搵得到 —— 所有條件要落喺同一次到訪上面。
              </div>
            )}
          </div>
        ) : (
          items.map((v) => (
            <VenueCard key={v.id} v={v} onClick={() => onOpen(v.id)} showDistrict />
          ))
        )}
      </div>
    </>
  );
}
